import { speciesLabel, ordinal } from './memberStats';
import { formatClubDate } from './clubTime';

interface LeaderboardData {
  categoryRankings: Record<string, any[]>;
  totalFishCount: number;
  totalCompetitors?: number;
  speciesBreakdown?: Record<string, number>;
}

interface WinnerPhoto {
  memberName: string;
}

interface CompetitionStats {
  totalRegistrations: number;
  totalSubmitters: number;
  newMembers?: number;
}

function buildSpeciesBreakdown(leaderboard: LeaderboardData): string {
  const breakdown = leaderboard.speciesBreakdown;
  if (!breakdown) {
    // Fall back to counting from categoryRankings
    const parts: string[] = [];
    for (const [species, entries] of Object.entries(leaderboard.categoryRankings || {})) {
      if (entries && entries.length > 0) {
        parts.push(`${speciesLabel(species)} ${entries.length}`);
      }
    }
    return parts.join(', ');
  }
  return Object.entries(breakdown)
    .filter(([, count]) => count > 0)
    .map(([species, count]) => `${speciesLabel(species)} ${count}`)
    .join(', ');
}

export function generateAnnouncementContent(
  eventTitle: string,
  eventDate: string,
  leaderboard: LeaderboardData,
  winnerPhoto: WinnerPhoto | null,
  stats?: CompetitionStats
): { title: string; content: string } {
  const dateStr = formatClubDate(eventDate, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  const title = `${eventTitle} — Competition Results`;
  const lines: string[] = [];

  lines.push(`What a fantastic day on the water! Here are the full results from ${eventTitle} held on ${dateStr}.`);
  lines.push('');

  // Day summary stats
  lines.push('**Day Summary**');
  if (stats?.totalRegistrations) lines.push(`Number of entrants: ${stats.totalRegistrations}`);
  if (stats?.totalSubmitters) lines.push(`Competitors who submitted: ${stats.totalSubmitters}`);
  if (stats?.newMembers) lines.push(`New members on the day: ${stats.newMembers}`);
  lines.push(`Total fish submitted: ${leaderboard.totalFishCount}`);
  const breakdown = buildSpeciesBreakdown(leaderboard);
  if (breakdown) lines.push(`Species breakdown: ${breakdown}`);
  lines.push('');

  // Per-species winners (top 3)
  lines.push('**Results by Species**');
  for (const [species, entries] of Object.entries(leaderboard.categoryRankings || {})) {
    if (!entries || entries.length === 0) continue;
    lines.push(`${speciesLabel(species)}`);
    entries.slice(0, 3).forEach((e: any, i: number) => {
      const kept = e.keptOrReleased === 'RELEASED' ? ' (released)' : '';
      lines.push(`  ${ordinal(i + 1)} — ${e.competitorName} ${e.lengthCm}cm${kept}`);
    });
    lines.push('');
  }

  // Happy Snaps winner
  if (winnerPhoto) {
    lines.push(`**Best Happy Snap 📸** — ${winnerPhoto.memberName}`);
    lines.push('');
  }

  lines.push('Congratulations to all our winners and thank you to everyone who came out and made it a great day!');
  lines.push('');
  lines.push('A big thank you to our sponsors for their continued support. 🙏');
  lines.push('');
  lines.push('See you at the next one! 🎣');

  return { title, content: lines.join('\n') };
}

export function generateSocialPost(
  eventTitle: string,
  eventDate: string,
  leaderboard: LeaderboardData,
  winnerPhoto: WinnerPhoto | null,
  stats?: CompetitionStats
): string {
  const dateStr = formatClubDate(eventDate, { day: 'numeric', month: 'long', year: 'numeric' });
  const lines: string[] = [];

  // Header
  lines.push(`🎣 COMP WRAP UP — ${eventTitle.toUpperCase()}`);
  lines.push(dateStr);
  lines.push('');

  // Stats block (mirrors the current manual post format)
  if (stats?.totalRegistrations) lines.push(`👥 Number of entrants: ${stats.totalRegistrations}`);
  if (stats?.totalSubmitters) lines.push(`🎣 People submitted: ${stats.totalSubmitters}`);
  if (stats?.newMembers) lines.push(`🆕 New members: ${stats.newMembers}`);
  lines.push(`🐟 Number of fish submitted: ${leaderboard.totalFishCount}`);

  const breakdown = buildSpeciesBreakdown(leaderboard);
  if (breakdown) lines.push(`📊 ${breakdown}`);
  lines.push('');

  // Winners per species
  lines.push('🏆 WINNERS');
  for (const [species, entries] of Object.entries(leaderboard.categoryRankings || {})) {
    if (!entries || entries.length === 0) continue;
    const winner = entries[0];
    const kept = winner.keptOrReleased === 'RELEASED' ? ' (released)' : '';
    lines.push(`${speciesLabel(species)}: ${winner.competitorName} — ${winner.lengthCm}cm${kept}`);
  }
  lines.push('');

  // Happy Snaps
  if (winnerPhoto) {
    lines.push(`📸 Best Happy Snap: ${winnerPhoto.memberName}`);
    lines.push('');
  }

  // Closing
  lines.push('Well done to all competitors and thank you to everyone who came out! 💪');
  lines.push('');
  lines.push('Thank you to all of our sponsors — see last page and support our local businesses! 🙏');
  lines.push('');
  lines.push('#TumbulgumFishingClub #TumbyFC #TweedRiver #fishing #fishingnNSW #mulloway #flathead #bream');

  return lines.join('\n');
}
